import { useEffect, useState } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { TelegramLogo, PaperPlaneTilt, TestTube } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

export default function TelegramSettings() {
  const [config, setConfig] = useState({ bot_token: "", chat_id: "", enabled: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fullToken, setFullToken] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/admin/settings/telegram`, { headers: getAuthHeaders() })
      .then((res) => {
        setConfig(res.data);
      })
      .catch((err) => logger.error("Telegram settings error:", err));
  }, []);

  const handleSave = async () => {
    const tokenToSave = fullToken || config.bot_token;
    if (!tokenToSave || !config.chat_id) {
      toast.error("Both Bot Token and Chat ID are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/settings/telegram`,
        { bot_token: tokenToSave, chat_id: config.chat_id, enabled: config.enabled },
        { headers: getAuthHeaders() }
      );
      toast.success("Telegram settings saved");
      setFullToken("");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/admin/settings/telegram/test`, {}, { headers: getAuthHeaders() });
      toast.success("Test message sent to Telegram!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test message");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="telegram-settings">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(0, 229, 255, 0.1)" }}>
          <TelegramLogo size={20} weight="duotone" className="text-[#00E5FF]" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Telegram Webhook Alerts
          </h3>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5">
            Receive instant alerts for withdrawals above ₹10,000
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-md border border-white/10 bg-white/[0.02]">
          <div>
            <p className="text-xs text-white">Enable Telegram Alerts</p>
            <p className="text-[10px] text-[#A1A1AA]">Send notifications to your Telegram chat</p>
          </div>
          <Switch
            data-testid="telegram-enabled-switch"
            checked={config.enabled}
            onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-1.5 block">Bot Token</label>
          <Input
            data-testid="telegram-bot-token-input"
            type="password"
            placeholder={config.configured ? "Token saved (enter new to update)" : "Enter Telegram Bot Token from @BotFather"}
            value={fullToken}
            onChange={(e) => setFullToken(e.target.value)}
            className="bg-transparent border-white/10 text-white placeholder:text-[#A1A1AA]/40 text-sm focus-visible:ring-[#00E5FF]"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-1.5 block">Chat ID</label>
          <Input
            data-testid="telegram-chat-id-input"
            placeholder="Enter Telegram Chat ID"
            value={config.chat_id}
            onChange={(e) => setConfig({ ...config, chat_id: e.target.value })}
            className="bg-transparent border-white/10 text-white placeholder:text-[#A1A1AA]/40 text-sm focus-visible:ring-[#00E5FF]"
          />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            data-testid="save-telegram-settings"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-colors disabled:opacity-50"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {saving ? "Saving..." : "Save Settings"}
          </button>
          <button
            data-testid="test-telegram"
            onClick={handleTest}
            disabled={testing || !config.configured}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs border border-white/20 text-white hover:border-white/40 transition-colors disabled:opacity-50"
          >
            <TestTube size={14} />
            {testing ? "Sending..." : "Send Test"}
          </button>
        </div>

        <div className="p-3 rounded-md border border-white/5 bg-white/[0.01] mt-2">
          <p className="text-[10px] text-[#A1A1AA]">
            <strong className="text-white">Setup:</strong> Message <span className="text-[#00E5FF]">@BotFather</span> on Telegram, use <span className="text-[#00E5FF]">/newbot</span> to create a bot and get your token.
            Forward a message from your chat to <span className="text-[#00E5FF]">@userinfobot</span> to get your Chat ID.
          </p>
        </div>
      </div>
    </div>
  );
}
